import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { setOnUnauthenticated } from '../lib/axiosInstance';
import { setCurrentRole } from '../lib/readOnlyMode';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

// Auto-logout after 4 hours of inactivity
const INACTIVITY_TIMEOUT_MS = 4 * 60 * 60 * 1000;

// Helper to format API errors
function formatApiErrorDetail(detail) {
    if (detail == null) return "Une erreur s'est produite. Veuillez réessayer.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = not authenticated
    const [loading, setLoading] = useState(true);
    const inactivityTimerRef = useRef(null);

    const performLogout = useCallback(async (silent = false) => {
        try {
            if (!silent) {
                await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
            }
        } catch (error) {
            // ignore
        } finally {
            setUser(false);
            setCurrentRole(null);
        }
    }, []);

    const resetInactivityTimer = useCallback(() => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }
        inactivityTimerRef.current = setTimeout(() => {
            performLogout(false);
        }, INACTIVITY_TIMEOUT_MS);
    }, [performLogout]);

    useEffect(() => {
        checkAuth();
        // Hook the axios interceptor: when refresh fails, log out silently
        setOnUnauthenticated(() => {
            setUser(false);
            setCurrentRole(null);
        });
    }, []);

    // Track activity to reset the inactivity timer (only when authenticated)
    useEffect(() => {
        if (!user) {
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
                inactivityTimerRef.current = null;
            }
            return;
        }

        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
        const handler = () => resetInactivityTimer();
        events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));
        resetInactivityTimer();

        return () => {
            events.forEach((ev) => window.removeEventListener(ev, handler));
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
                inactivityTimerRef.current = null;
            }
        };
    }, [user, resetInactivityTimer]);

    const setUserAndRole = useCallback((u) => {
        setUser(u);
        setCurrentRole(u && typeof u === 'object' ? u.role : null);
    }, []);

    const checkAuth = async () => {
        try {
            const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
            setUserAndRole(data);
        } catch (error) {
            setUserAndRole(false);
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        try {
            const { data } = await axios.post(
                `${API}/api/auth/login`,
                { email, password },
                { withCredentials: true }
            );
            setUserAndRole(data);
            return { success: true };
        } catch (error) {
            const detail = error.response?.data?.detail;
            return { 
                success: false, 
                error: formatApiErrorDetail(detail) || error.message 
            };
        }
    };

    const register = async (email, password, name) => {
        try {
            const { data } = await axios.post(
                `${API}/api/auth/register`,
                { email, password, name },
                { withCredentials: true }
            );
            setUserAndRole(data);
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
            };
        }
    };

    const logout = async () => {
        await performLogout(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
